import { type RefObject, useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

interface HeroFieldsProps {
  interactive?: boolean;
  sectionRef?: RefObject<HTMLElement>;
  compact?: boolean;
}

const HeroFields = ({ interactive = false, sectionRef, compact = false }: HeroFieldsProps) => {
  const reduced = useReducedMotion();
  const fields = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (!interactive || reduced || window.innerWidth < 700 || !matchMedia("(pointer:fine)").matches) return;
    const section = sectionRef?.current;
    if (!section) return;
    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const update = () => {
      const bounds = section.getBoundingClientRect();
      const x = ((pointerX - bounds.left) / bounds.width - 0.5) * 2;
      const y = ((pointerY - bounds.top) / bounds.height - 0.5) * 2;
      [14, 10, 18].forEach((amount, index) => {
        const field = fields.current[index];
        if (field) field.style.translate = `${x * amount}px ${y * amount}px`;
      });
      frame = 0;
    };
    const onPointerMove = (event: PointerEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(update);
    };
    const onScroll = () => {
      if (document.hidden) return;
      const bounds = section.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > innerHeight) return;
      fields.current.forEach((field) => field?.style.setProperty("--scroll-shift", `${Math.max(-6, Math.min(6, -bounds.top * 0.15))}px`));
    };
    section.addEventListener("pointermove", onPointerMove);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      section.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [interactive, reduced, sectionRef]);

  const size = compact ? "h-56 w-56 md:h-60 md:w-60" : "h-80 w-80";
  const middleSize = compact ? "h-48 w-48 md:h-52 md:w-52" : "h-72 w-72";
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div ref={(element) => { fields.current[0] = element; }} className={`hero-field-a absolute ${compact ? "-left-36 -top-20" : "-left-24 top-10"} ${size} rounded-full bg-[rgba(31,163,163,.6)] opacity-80 blur-[60px] mix-blend-multiply transition-transform duration-[600ms]`} style={{ transform: "translateY(var(--scroll-shift,0px))" }} />
      <div ref={(element) => { fields.current[1] = element; }} className={`hero-field-b absolute ${compact ? "-right-20 top-8" : "right-[28%] top-20"} ${middleSize} rounded-full bg-[rgba(31,78,107,.45)] opacity-80 blur-[60px] mix-blend-multiply transition-transform duration-[600ms]`} style={{ transform: "translateY(var(--scroll-shift,0px))" }} />
      <div ref={(element) => { fields.current[2] = element; }} className={`hero-field-c absolute ${compact ? "-right-36 -bottom-24" : "-right-20 bottom-4"} ${size} rounded-full bg-[rgba(224,182,60,.55)] opacity-80 blur-[60px] mix-blend-multiply transition-transform duration-[600ms]`} style={{ transform: "translateY(var(--scroll-shift,0px))" }} />
    </div>
  );
};

export default HeroFields;